import React, { useEffect, useRef, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ProjectMetricService from 'service/ClientTransmoraService/ProjectMetricService';
import LoadingSpinner from 'component/LoadingSpinner';

interface MetricValueItem {
  id: string | null;
  name: string;
  icon: string;
  colorHex: string;
  rules: string;
}

interface MetricItem {
  id: string | null;
  name: string;
  collapsed: boolean;
  metricValue: MetricValueItem[];
}

interface DeleteTarget {
  type: 'metric' | 'value';
  metricIndex: number;
  valueIndex?: number;
  label: string;
}

const ProjectMetricForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useSelector((state: any) => state.auth);
  const projectMetricService = ProjectMetricService();

  const [metrics, setMetrics] = useState<MetricItem[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  // Delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Drag refs
  const dragMetricIndex = useRef<number | null>(null);
  const dragValueIndex = useRef<{ metricIndex: number; valueIndex: number } | null>(null);

  // ── Fetch ──
  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token || !id) return;
    setIsLoadingDetail(true);
    try {
      const res = await projectMetricService.ProjectMetricDetail(token, id);
      const list = res.data?.metric ?? [];
      setMetrics(list.map((m: any) => ({
        id: m.id,
        name: m.name,
        sort: m.sort,
        collapsed: true,
        metricValue: (m.metricValue ?? []).map((mv: any) => ({
          id: mv.id,
          name: mv.name,
          icon: mv.icon,
          colorHex: mv.colorHex,
          rules: mv.rules,
          sort: mv.sort,
        })),
      })));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  // ── Metric handlers ──
  const handleAddMetric = () => {
    setMetrics((prev) => [
      ...prev,
      { id: null, name: '', collapsed: false, metricValue: [] },
    ]);
  };

  const handleMetricChange = (index: number, value: string) => {
    setMetrics((prev) =>
      prev.map((item, i) => (i === index ? { ...item, name: value } : item))
    );
  };

  const handleToggleCollapse = (index: number) => {
    setMetrics((prev) =>
      prev.map((item, i) => (i === index ? { ...item, collapsed: !item.collapsed } : item))
    );
  };

  // ── Metric drag ──
  const handleMetricDragStart = (index: number) => {
    dragMetricIndex.current = index;
  };

  const handleMetricDragEnter = (index: number) => {
    if (dragMetricIndex.current === null || dragMetricIndex.current === index) return;
    setMetrics((prev) => {
      const updated = [...prev];
      const dragged = updated.splice(dragMetricIndex.current!, 1)[0];
      updated.splice(index, 0, dragged);
      dragMetricIndex.current = index;
      return updated;
    });
  };

  const handleMetricDragEnd = () => {
    dragMetricIndex.current = null;
  };

  // ── Value handlers ──
  const handleAddValue = (metricIndex: number) => {
    setMetrics((prev) =>
      prev.map((item, i) =>
        i === metricIndex
          ? {
              ...item,
              collapsed: false,
              metricValue: [
                ...item.metricValue,
                { id: null, name: '', icon: '', colorHex: '', rules: '' },
              ],
            }
          : item
      )
    );
  };

  const handleValueChange = (
    metricIndex: number,
    valueIndex: number,
    field: keyof MetricValueItem,
    value: string
  ) => {
    setMetrics((prev) =>
      prev.map((item, i) =>
        i === metricIndex
          ? {
              ...item,
              metricValue: item.metricValue.map((mv, vi) =>
                vi === valueIndex ? { ...mv, [field]: value } : mv
              ),
            }
          : item
      )
    );
  };

  // ── Value drag ──
  const handleValueDragStart = (metricIndex: number, valueIndex: number) => {
    dragValueIndex.current = { metricIndex, valueIndex };
  };

  const handleValueDragEnter = (metricIndex: number, valueIndex: number) => {
    if (!dragValueIndex.current) return;
    if (
      dragValueIndex.current.metricIndex !== metricIndex ||
      dragValueIndex.current.valueIndex === valueIndex
    ) return;

    setMetrics((prev) =>
      prev.map((item, i) => {
        if (i !== metricIndex) return item;
        const updated = [...item.metricValue];
        const dragged = updated.splice(dragValueIndex.current!.valueIndex, 1)[0];
        updated.splice(valueIndex, 0, dragged);
        dragValueIndex.current = { metricIndex, valueIndex };
        return { ...item, metricValue: updated };
      })
    );
  };

  const handleValueDragEnd = () => {
    dragValueIndex.current = null;
  };

  // ── Delete modal ──
  const handleDeleteMetricClick = (metricIndex: number) => {
    setDeleteTarget({
      type: 'metric',
      metricIndex,
      label: metrics[metricIndex].name || `Metric #${metricIndex + 1}`,
    });
    setShowDeleteModal(true);
  };

  const handleDeleteValueClick = (metricIndex: number, valueIndex: number) => {
    const mv = metrics[metricIndex].metricValue[valueIndex];
    setDeleteTarget({
      type: 'value',
      metricIndex,
      valueIndex,
      label: mv.name || `Value #${valueIndex + 1}`,
    });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    if (deleteTarget.type === 'metric') {
      setMetrics((prev) => prev.filter((_, i) => i !== deleteTarget.metricIndex));
    } else if (deleteTarget.type === 'value' && deleteTarget.valueIndex !== undefined) {
      setMetrics((prev) =>
        prev.map((item, i) =>
          i === deleteTarget.metricIndex
            ? {
                ...item,
                metricValue: item.metricValue.filter((_, vi) => vi !== deleteTarget.valueIndex),
              }
            : item
        )
      );
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  // ── Submit ──
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = profile?.data.token;
    if (!token || !id) return;

    setIsLoadingSave(true);
    try {
      const payload = {
        projectId: id,
        metric: metrics.map((item, metricIndex) => ({
          id: item.id || '',
          name: item.name,
          sort: metricIndex + 1,
          metricValue: item.metricValue.map((mv, valueIndex) => ({
            id: mv.id || '',
            name: mv.name,
            icon: mv.icon,
            colorHex: mv.colorHex,
            rules: mv.rules,
            sort: valueIndex + 1,
          })),
        })),
      };

      await projectMetricService.ProjectMetricSave(token, payload);
      alert('Project Metric berhasil disimpan.');
      navigate('/client-project-metric');
    } catch (err: any) {
      alert(err.message || 'Gagal menyimpan data.');
      console.error(err);
    } finally {
      setIsLoadingSave(false);
    }
  };

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">Manage Project Metric</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>

              {/* Add Metric Button */}
              <div className="d-flex justify-content-end mb-3">
                <button type="button" className="btn btn-primary px-4" onClick={handleAddMetric}>
                  Add Metric
                </button>
              </div>

              {/* Metric Cards */}
              <div className="mb-4">
                {metrics.length === 0 && (
                  <p className="text-muted text-center fst-italic py-4">
                    Belum ada metric. Klik "Add Metric" untuk menambahkan.
                  </p>
                )}

                {metrics.map((item, metricIndex) => (
                  <div
                    key={metricIndex}
                    className="d-flex align-items-start mb-3"
                    draggable
                    onDragStart={() => handleMetricDragStart(metricIndex)}
                    onDragEnter={() => handleMetricDragEnter(metricIndex)}
                    onDragEnd={handleMetricDragEnd}
                    onDragOver={(e) => e.preventDefault()}
                    style={{ cursor: 'grab' }}
                  >
                    {/* Metric grip */}
                    <div className="me-2 text-secondary mt-3" style={{ fontSize: '18px', paddingTop: '6px', cursor: 'grab' }}>
                      <i className="fa-solid fa-grip-vertical"></i>
                    </div>

                    {/* Card */}
                    <div
                      className="flex-grow-1 p-3"
                      style={{ border: '1px solid #ccc', borderRadius: '10px', background: 'white' }}
                    >
                      {/* Name + Delete */}
                      <div className="d-flex align-items-end gap-3">
                        <div className="flex-grow-1">
                          <label className="form-label fw-semibold mb-1" style={{ fontSize: '13px' }}>Name</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.name}
                            onChange={(e) => handleMetricChange(metricIndex, e.target.value)}
                            required
                          />
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm border-0 mb-1"
                          onClick={() => handleDeleteMetricClick(metricIndex)}
                          title="Hapus metric"
                        >
                          <i className="fa-solid fa-trash"></i>
                        </button>
                      </div>

                      {/* Collapse toggle + Add Value */}
                      <div className="d-flex align-items-center justify-content-between mt-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                          onClick={() => handleToggleCollapse(metricIndex)}
                          style={{ fontSize: '13px' }}
                        >
                          {item.metricValue.length} Values{' '}
                          <i className={`fa-solid fa-chevron-${item.collapsed ? 'down' : 'up'} ms-1`} style={{ fontSize: '11px' }}></i>
                        </button>

                        {!item.collapsed && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            onClick={() => handleAddValue(metricIndex)}
                            style={{ fontSize: '13px' }}
                          >
                            <i className="fa-solid fa-plus me-1"></i>Add Value
                          </button>
                        )}
                      </div>

                      {/* Metric Values */}
                      {!item.collapsed && (
                        <div className="mt-2">
                          {item.metricValue.length === 0 && (
                            <p className="text-muted fst-italic mb-0" style={{ fontSize: '12px' }}>
                              Belum ada value. Klik "+ Add Value" untuk menambahkan.
                            </p>
                          )}

                          {item.metricValue.map((mv, valueIndex) => (
                            <div
                              key={valueIndex}
                              className="d-flex align-items-end gap-2 mb-2"
                              draggable
                              onDragStart={(e) => { e.stopPropagation(); handleValueDragStart(metricIndex, valueIndex); }}
                              onDragEnter={(e) => { e.stopPropagation(); handleValueDragEnter(metricIndex, valueIndex); }}
                              onDragEnd={(e) => { e.stopPropagation(); handleValueDragEnd(); }}
                              onDragOver={(e) => e.preventDefault()}
                              style={{ cursor: 'grab' }}
                            >
                              {/* Value grip */}
                              <div className="text-secondary" style={{ fontSize: '14px', cursor: 'grab', paddingBottom: '6px' }}>
                                <i className="fa-solid fa-grip-vertical"></i>
                              </div>

                              <div style={{ width: '180px' }}>
                                {valueIndex === 0 && <label className="form-label mb-1" style={{ fontSize: '12px' }}>Name</label>}
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={mv.name}
                                  onChange={(e) => handleValueChange(metricIndex, valueIndex, 'name', e.target.value)}
                                  placeholder="Name"
                                  required
                                />
                              </div>

                              <div style={{ width: '160px' }}>
                                {valueIndex === 0 && <label className="form-label mb-1" style={{ fontSize: '12px' }}>Icon</label>}
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={mv.icon}
                                  onChange={(e) => handleValueChange(metricIndex, valueIndex, 'icon', e.target.value)}
                                  placeholder="icon.svg"
                                />
                              </div>

                              <div style={{ width: '140px' }}>
                                {valueIndex === 0 && <label className="form-label mb-1" style={{ fontSize: '12px' }}>Color Hex</label>}
                                <div className="d-flex align-items-center gap-1">
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={mv.colorHex}
                                    onChange={(e) => handleValueChange(metricIndex, valueIndex, 'colorHex', e.target.value)}
                                    placeholder="FFFFFF"
                                    maxLength={6}
                                  />
                                  {mv.colorHex && (
                                    <div style={{
                                      width: '24px', height: '24px', borderRadius: '4px',
                                      background: `#${mv.colorHex}`, border: '1px solid #ccc', flexShrink: 0,
                                    }} />
                                  )}
                                </div>
                              </div>

                              <div className="flex-grow-1">
                                {valueIndex === 0 && <label className="form-label mb-1" style={{ fontSize: '12px' }}>Rules</label>}
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={mv.rules}
                                  onChange={(e) => handleValueChange(metricIndex, valueIndex, 'rules', e.target.value)}
                                  placeholder="Rules"
                                />
                              </div>

                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm border-0"
                                onClick={() => handleDeleteValueClick(metricIndex, valueIndex)}
                                title="Hapus value"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="w-100 d-flex justify-content-end">
                <Link to="/client-project-metric">
                  <button type="button" className="btn btn-outline-primary">Cancel</button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary d-flex justify-content-center align-items-center ms-2"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && (
                    <>
                      <LoadingSpinner reverse={true} />
                      <p className="ms-2"></p>
                    </>
                  )}
                  Save
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={handleCloseDeleteModal}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content p-4">
              <h5 className="fw-bold mb-1">
                Hapus {deleteTarget.type === 'metric' ? 'Metric' : 'Value'}
              </h5>
              <hr />
              <p className="mb-0">
                Apakah Anda yakin ingin menghapus{' '}
                {deleteTarget.type === 'metric' ? 'metric' : 'value'}{' '}
                <strong>"{deleteTarget.label}"</strong>?
              </p>
              {deleteTarget.type === 'metric' && (
                <p className="text-danger small mt-1 mb-0">
                  Semua value di dalam metric ini juga akan ikut terhapus.
                </p>
              )}
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button className="btn btn-outline-secondary px-4" onClick={handleCloseDeleteModal} style={{ borderRadius: '8px' }}>
                  Batal
                </button>
                <button className="btn btn-danger px-4" onClick={handleConfirmDelete} style={{ borderRadius: '8px' }}>
                  <i className="fa-solid fa-trash me-2"></i>Hapus
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectMetricForm;